import type { ThemePreference } from "@prisma/design-tokens";
import * as Menu from "@radix-ui/react-dropdown-menu";
import { Check, Monitor, Moon, Sun } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { applyTheme, effectiveTheme, getThemePreference } from "../lib/theme";

const OPTIONS: { value: ThemePreference; icon: typeof Sun }[] = [
  { value: "system", icon: Monitor },
  { value: "light", icon: Sun },
  { value: "dark", icon: Moon },
];

/** Theme control (ADR 0016 rule 8): the locked screens show a sun/toggle pill; the menu adds
 *  System, which is the default (doc 06 section 6). */
export function ThemeControl() {
  const { t } = useTranslation();
  const [pref, setPref] = useState<ThemePreference>(getThemePreference);
  const dark = effectiveTheme(pref) === "dark";
  const choose = (v: ThemePreference) => {
    applyTheme(v);
    setPref(v);
  };
  return (
    <Menu.Root>
      <Menu.Trigger
        aria-label={`${t("shell.theme_label")}: ${t(`shell.theme_${pref}`)}`}
        className="flex h-11 items-center gap-2 rounded-[var(--radius-card)] border border-line bg-surface px-3 text-ink-2 hover:text-ink"
      >
        <Sun aria-hidden="true" className="size-[18px]" />
        <span
          aria-hidden="true"
          className="relative h-6 w-[42px] rounded-full bg-soft transition-colors"
          data-on={dark}
        >
          <span
            className={`absolute top-0.5 size-5 rounded-full bg-[image:var(--gradient-primary-button)] transition-transform ${dark ? "translate-x-[18px]" : "translate-x-0.5"}`}
          />
        </span>
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Content
          align="end"
          sideOffset={8}
          className="z-50 min-w-44 rounded-[var(--radius-card)] border border-line bg-surface p-1 shadow-[var(--shadow-card)]"
        >
          <Menu.Label className="px-3 py-2 text-[12px] text-ink-3">{t("shell.theme_label")}</Menu.Label>
          {OPTIONS.map(({ value, icon: Icon }) => (
            <Menu.Item
              key={value}
              onSelect={() => choose(value)}
              className="flex cursor-pointer items-center gap-2 rounded-[var(--radius-control)] px-3 py-2 text-[14px] text-ink outline-none data-[highlighted]:bg-elevated"
            >
              <Icon aria-hidden="true" className="size-4 text-ink-2" />
              <span className="flex-1">{t(`shell.theme_${value}`)}</span>
              {pref === value && <Check aria-hidden="true" className="size-4 text-violet" />}
            </Menu.Item>
          ))}
        </Menu.Content>
      </Menu.Portal>
    </Menu.Root>
  );
}
